import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Paper, Button, Typography } from '../../../../../components/mui/Core/index'
import { useTheme } from '../../../../../components/mui/Styles/index'
import { useDispatch } from 'react-redux'
import { setAllocationQty } from '../../../hold-allocation-detail-slice'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CheckIcon from '@mui/icons-material/Check'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'

function AllocationTable({ allocationLines, onPrefill, onSubmit, onDefer, isSubmitting, orderId }) {
  const theme = useTheme()
  const dispatch = useDispatch()

  const rows = allocationLines.map((l) => ({
    id: l.lineNumber,
    lineNumber: l.lineNumber,
    sku: l.sku,
    orderOpenQty: l.orderOpenQty,
    availableToAllocate: l.availableToAllocate,
    committedQty: l.committedQty ?? 0,
    ppsQty: l.ppsQuantity ?? 0,
    suggestedAllocateQty: l.suggestedAllocateQty,
    allocateQty: l.allocateQty != null ? l.allocateQty : '',
    source: l.source,
  }))

  const columns = [
    { field: 'lineNumber', headerName: '#', flex: 0.2, minWidth: 60, headerAlign: 'right', align: 'right' },
    { field: 'sku', headerName: 'SKU / Item', flex: 1, minWidth: 160 },
    {
      field: 'orderOpenQty',
      headerName: 'Ordered Qty',
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'availableToAllocate',
      headerName: 'Available',
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      renderCell: (p) => {
        const enough = p.value >= p.row.orderOpenQty
        return (
          <Box sx={{ color: enough ? '#2a7b5a' : '#cc3f0c', fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem' }}>
            {p.value}
          </Box>
        )
      },
    },
    {
      field: 'committedQty',
      headerName: 'Committed',
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      renderCell: (p) => (
        <Box sx={{ color: '#606060', fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem' }}>
          {p.value ?? 0}
        </Box>
      ),
    },
    {
      field: 'ppsQty',
      headerName: 'On PPS',
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      renderCell: (p) => (
        <Box sx={{ color: p.value > 0 ? '#2a7b5a' : '#b7b7b7', fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem', fontWeight: p.value > 0 ? 600 : 400 }}>
          {p.value ?? 0}
        </Box>
      ),
    },
    {
      field: 'suggestedAllocateQty',
      headerName: 'Suggested',
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      renderCell: (p) => (
        <Box sx={{ color: '#606060', fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem' }}>
          {p.value}
        </Box>
      ),
    },
    {
      field: 'allocateQty',
      headerName: 'Allocate Qty',
      flex: 0.5,
      minWidth: 110,
      headerAlign: 'right',
      align: 'right',
      editable: true,
      renderCell: (p) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            color: p.value !== '' ? '#145a4b' : '#b7b7b7',
            fontFamily: 'AtlasGroteskRegular',
            fontSize: '0.85rem',
            fontWeight: p.value !== '' ? 600 : 400,
            border: '1px dashed #bebebe',
            borderRadius: 1,
            px: 1,
            cursor: 'text',
          }}
        >
          {p.value !== '' ? p.value : 'Click to edit'}
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 0.3,
      minWidth: 70,
      renderCell: (p) => (
        <Box sx={{ fontSize: '0.72rem', fontFamily: 'AtlasGroteskRegular', color: '#606060', backgroundColor: '#f2f2f2', borderRadius: 1, px: 0.75 }}>
          {p.value}
        </Box>
      ),
    },
  ]

  const processRowUpdate = (newRow) => {
    dispatch(setAllocationQty({ lineNumber: newRow.lineNumber, qty: Number(newRow.allocateQty) || 0 }))
    return newRow
  }

  const gridStyle = {
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
    '& .MuiDataGrid-cell--editable': {
      cursor: 'text',
    },
  }

  return (
    <Box>
      <Paper>
        <DataGrid
          sx={gridStyle}
          rows={rows}
          columns={columns}
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) => console.error(err)}
          disableColumnFilter
          disableRowSelectionOnClick
          autoHeight
        />
      </Paper>
      {/* Action row */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2, alignItems: 'center' }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<AutoFixHighIcon />}
          onClick={() => onPrefill(orderId)}
          disabled={isSubmitting}
          sx={{ fontFamily: 'AtlasGroteskRegular', textTransform: 'none', fontSize: '0.82rem', borderColor: '#2db47d', color: '#2db47d', '&:hover': { backgroundColor: '#e8f5e9', borderColor: '#145a4b', color: '#145a4b' } }}
        >
          Prefill Suggested
        </Button>
        <Button
          variant='contained'
          size='small'
          startIcon={<CheckIcon />}
          onClick={() => onSubmit(orderId)}
          disabled={isSubmitting}
          color='secondary'
          sx={{ fontFamily: 'AtlasGroteskRegular', textTransform: 'none', fontSize: '0.82rem' }}
        >
          {isSubmitting ? 'Submitting…' : 'Submit Allocation'}
        </Button>
        <Button
          variant='outlined'
          size='small'
          startIcon={<PauseCircleIcon />}
          onClick={() => onDefer(orderId)}
          disabled={isSubmitting}
          sx={{ fontFamily: 'AtlasGroteskRegular', textTransform: 'none', fontSize: '0.82rem', borderColor: '#b7b7b7', color: '#606060', '&:hover': { borderColor: '#606060' } }}
        >
          Defer
        </Button>
        <Typography sx={{ ml: 'auto', fontSize: '0.75rem', fontFamily: 'AtlasGroteskRegular', color: '#b7b7b7' }}>
          Click a cell in &quot;Allocate Qty&quot; to edit
        </Typography>
      </Box>
    </Box>
  )
}

AllocationTable.propTypes = {
  allocationLines: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPrefill: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDefer: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  orderId: PropTypes.string.isRequired,
}

export default AllocationTable
